import Modal from "components/Modals/Modal";
import React, { useCallback, useState } from "react";
import { Checkbox } from "components/Graphs/Checkbox";
import { MoonIcon } from "@heroicons/react/24/solid";
import { STORAGE_KEYS } from "constants/localStorage";

export interface StopModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onStop?: () => void;
  storageCostPerHr?: number;
}

export const StopModal: React.FC<StopModalProps> = ({
  show,
  setShow,
  onStop = () => {},
  storageCostPerHr,
}) => {
  const [dismissChecked, setDismissChecked] = useState(false);
  const [storageChargeAcknowledged, setStorageChargeAcknowledged] = useState(false);

  const showStorageCheckbox =
    storageCostPerHr !== undefined && storageCostPerHr > 0;

  const resetState = useCallback(() => {
    setDismissChecked(false);
    setStorageChargeAcknowledged(false);
  }, []);

  const handleConfirm = () => {
    if (dismissChecked) {
      localStorage.setItem(STORAGE_KEYS.stopFreeTierModalDismissed, "true");
    }
    onStop();
    setShow(false);
  };

  const isConfirmEnabled = !showStorageCheckbox || storageChargeAcknowledged;

  return (
    <Modal
      isOpen={show}
      setOpen={setShow}
      onClose={() => {
        resetState();
        setShow(false);
      }}
      title=""
      hideIcon
      body={
        <div>
          <div className="flex items-center gap-2 mb-4">
            <MoonIcon className="h-5 w-5 text-white" />
            <h3 className="text-lg font-medium text-primary">
              Stop Instance
            </h3>
          </div>
          <ul className="list-disc pl-5 text-sm space-y-1 text-secondary">
            {(storageCostPerHr === undefined || storageCostPerHr > 0) && (
              <>
                <li>Compute charges stop but storage charges will continue</li>
                <li>
                  To stop storage charges, <strong>DELETE</strong> the
                  environment instead
                </li>
              </>
            )}
            <li>
              You can restart <strong>IF</strong> the instance type is available
              at the time of restart
            </li>
          </ul>
          {/* Storage charge acknowledgement checkbox */}
          {showStorageCheckbox && (
            <div
              className="mt-4 pt-3 border-t border-zinc-800"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`flex items-center gap-3 p-3 rounded-md border transition-colors ${
                  storageChargeAcknowledged
                    ? "border-highlight bg-highlight/10"
                    : "border-zinc-700 bg-zinc-900 hover:border-zinc-500"
                }`}
              >
                <Checkbox
                  id="storage-charge-acknowledged"
                  className="mt-0.5"
                  checked={storageChargeAcknowledged}
                  onCheckedChange={(checked) =>
                    setStorageChargeAcknowledged(checked === true)
                  }
                />
                <label
                  htmlFor="storage-charge-acknowledged"
                  className="text-sm text-primary cursor-pointer leading-snug"
                >
                  I understand I will be charged{" "}
                  <span className="text-highlight font-semibold">
                    ${storageCostPerHr!.toFixed(2)}/hr
                  </span>{" "}
                  for storage while this instance is stopped
                  {!storageChargeAcknowledged && (
                    <span className="text-rose-400 ml-0.5">*</span>
                  )}
                </label>
              </div>
            </div>
          )}

          {/* Dismiss checkbox — free tier only */}
          {!showStorageCheckbox && (
            <div
              className="flex items-center gap-2 mt-4"
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                id="dismiss-stop-modal"
                checked={dismissChecked}
                onCheckedChange={(checked) =>
                  setDismissChecked(checked === true)
                }
              />
              <label
                htmlFor="dismiss-stop-modal"
                className="text-xs text-secondary cursor-pointer"
              >
                Don&apos;t show this again
              </label>
            </div>
          )}
        </div>
      }
      cancelLabel="Cancel"
      confirmLabel="Stop"
      disableSuccess={!isConfirmEnabled}
      onSuccess={handleConfirm}
    />
  );
};
